// ===========================================================================
// Module B — High Risk Patient Surveillance · Service layer
//
// Hoy lee unmanaged_risk.json de los dumps diarios de DataDashCareManager.
// El score compuesto, la cadencia de touchpoints y la acción recomendada NO
// existen en el dump: se derivan DETERMINÍSTICAMENTE (reglas + cipId) para que
// la UI sea estable. SIMULADO hasta que el backend exponga
// clinical.TAB_SURVEILLANCE_PANEL / TAB_SURVEILLANCE_EVENTS.
// ===========================================================================

/**
 * Score compuesto de vigilancia (0-1): riesgo + costo + utilización + gaps.
 * Réplica simplificada del motor de scoring del SP futuro.
 */
function computeRiskScore(p) {
  const indicator = Math.min((p.globalIndicator ?? 0) / 7, 1); // 0-7
  const gaps = Math.min((p.openCareGaps ?? 0) / 6, 1);
  const cost = Math.min((p.totalCost ?? 0) / 150000, 1);
  const er = Math.min((p.erEvents ?? 0) / 4, 1);
  const triggers = Math.min((p.triggers ?? 0) / 2, 1);
  const score =
    0.35 * indicator + 0.2 * gaps + 0.2 * cost + 0.15 * er + 0.1 * triggers;
  return Math.round(score * 100) / 100;
}

/** Acción recomendada por reglas (espejo del motor backend futuro). */
function deriveRecommendedAction(p, score) {
  if (p.healthStatus === "End of Life" || p.healthStatus === "Complex" || score >= 0.75) {
    return "REFER_CM";
  }
  if ((p.openCareGaps ?? 0) >= 3) return "GAP_VISIT";
  if (p.isHighCost) return "COST_REVIEW";
  return "TOUCHPOINT";
}

/**
 * Días desde el último touchpoint de vigilancia — SIMULADO (cipId % 45)
 * hasta que exista clinical.TAB_SURVEILLANCE_EVENTS.
 */
function deriveDaysSinceTouchpoint(cipId) {
  return Number(cipId) % 45;
}

/**
 * Obtiene el panel de vigilancia: pacientes de alto riesgo SIN care management,
 * priorizados por score compuesto.
 *
 * FUTURO ENDPOINT BACKEND: GET /api/Surveillance/panel?locationKey={key}
 * (respaldado por clinical.TAB_SURVEILLANCE_PANEL). Hoy: dump JSON local +
 * derivación determinística simulada.
 *
 * @param {string} locationKey - Key de la location (e.g. "fort_myers")
 *
 * Ejemplo de respuesta:
 * {
 *   "summary": { "atRiskNoCM": 155, "complexNoCM": 18, "eolNoCM": 7, "total": 180 },
 *   "patients": [
 *     {
 *       "cipId": 18750261076, "name": "MCQUINN, MICHELE",
 *       "healthStatus": "End of Life", "globalIndicator": 7, "openCareGaps": 5,
 *       "totalCost": 107791.73, "triggers": 1, "isHighCost": true,
 *       // —— derivados (simulados) ——
 *       "riskScore": 0.82, "recommendedAction": "REFER_CM",
 *       "daysSinceTouchpoint": 31, "touchpointOverdue": true
 *     }
 *   ]
 * }
 *
 * @returns {Promise<{summary: Object, patients: Object[]}>}
 */
export async function getSurveillancePanel(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/unmanaged_risk.json`
  );
  const data = module.default || module;

  const patients = (data.patients || []).map((p) => {
    const riskScore = computeRiskScore(p);
    const daysSinceTouchpoint = deriveDaysSinceTouchpoint(p.cipId);
    return {
      ...p,
      riskScore,
      recommendedAction: deriveRecommendedAction(p, riskScore),
      daysSinceTouchpoint,
      // Cadencia simulada: alto riesgo debería tener touchpoint cada 30 días
      touchpointOverdue: daysSinceTouchpoint > 30,
    };
  });

  patients.sort((a, b) => b.riskScore - a.riskScore);

  return { summary: data.summary || {}, patients };
}
